import React, { useContext, useEffect, useState, useRef } from 'react';
import { useTranslations, useLocale } from 'next-intl';

import {
  arrow,
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useDismiss,
  useRole,
  useClick,
  useInteractions,
  useTransitionStyles,
  FloatingArrow,
  FloatingFocusManager,
  useId
} from '@floating-ui/react';

import Headline from '@components/atoms/headline/headline';

import { WishesContext } from '../../contexts/wishesProvider';

import { colorMapping } from '../../helpers/generateSpherePoints';

import { WishIcon } from '../icons/icons';

import StyledWish from './wish.style';

const Wish = () => {
  const t = useTranslations();
  const locale = useLocale();
  const { categories, activeWish, setActiveWish } = useContext(WishesContext);
  const category = categories.filter(c => c.id === activeWish?.categories[0]);

  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef(null);

  const { refs, floatingStyles, middlewareData, context } = useFloating({
    open: isOpen,
    onOpenChange: () => {
      setIsOpen(isOpen);
      setActiveWish(null);
    },
    placement: 'bottom',
    middleware: [
      offset({ mainAxis: 30, crossAxis: 0 }),
      flip({ fallbackAxisSideDirection: 'none' }),
      shift({ padding: 15 }),
      arrow({ element: arrowRef })
    ],
    whileElementsMounted: autoUpdate
  });

  const ARROW_WIDTH = 49;
  const ARROW_HEIGHT = 18; // Size of the arrow inside the square..
  const arrowX = middlewareData.arrow?.x ?? 0;
  const transformX = arrowX + ARROW_WIDTH / 2;

  const { isMounted, styles } = useTransitionStyles(context, {
    initial: {
      transform: 'scale(0)'
    },
    open: {
      transform: 'scale(1)'
    },
    common: {
      transformOrigin: `${transformX}px ${-ARROW_HEIGHT}px`
    }
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  const headingId = useId();

  useEffect(() => {
    if (activeWish) setIsOpen(true);
  }, [activeWish]);

  if (!activeWish) return null;

  return (
    <StyledWish $wishColor={colorMapping(category[0]?.id, true)}>
      {isMounted && (
        <>
          <button
            className="wish__trigger"
            type="button"
            aria-label="Wish trigger"
            ref={refs.setReference}
            {...getReferenceProps()}
          >
            {activeWish && category[0] && (
              <img
                src={category[0].image.url}
                srcSet={category[0].image.srcset}
                sizes="(min-width: 768px) 142px, 112px"
                alt=""
              />
            )}
          </button>

          <FloatingFocusManager context={context} modal={false} tabIndex="-1">
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              aria-labelledby={headingId}
              {...getFloatingProps()}
            >
              <div style={styles} className="wish__panel">
                <div className="wish__icon">
                  <div className="wish__icon-inner">
                    <WishIcon />
                  </div>
                </div>
                <div className="wish__wrapper">
                  <div className="wish__header">
                    <Headline id={headingId} tag="h2" main>
                      {activeWish.title[locale === 'de' ? 0 : 1]}
                    </Headline>
                  </div>
                  <div className="wish__content">
                    {category[0] && (
                      <div className="wish__tag">
                        <p>{category[0].title[locale === 'de' ? 0 : 1]}</p>
                      </div>
                    )}
                    {activeWish.userResidence && (
                      <div className="wish__tag">
                        <p>{activeWish.userResidence}</p>
                      </div>
                    )}
                    {activeWish.userAgeGroup && (
                      <div className="wish__tag">
                        <p>{activeWish.userAgeGroup}</p>
                      </div>
                    )}
                    {activeWish.userGender && (
                      <div className="wish__tag">
                        <p>{t(`wishes.gender.${activeWish.userGender}`)}</p>
                      </div>
                    )}
                  </div>
                </div>
                <FloatingArrow
                  ref={arrowRef}
                  context={context}
                  stroke="#b2b2b2"
                  strokeWidth={2}
                  width={49}
                  style={{ transform: 'translateY(1px)' }}
                  d="M46.5703 49C42.7596 48.8615 38.7631 42.3869 36.1403 38.1377C35.6733 37.3811 35.2498 36.6951 34.8787 36.128C30.3753 29.2456 18.8287 29.1566 14.2506 36.0026C13.8395 36.6173 13.3667 37.3758 12.8435 38.215C10.1782 42.4906 6.20592 48.8626 2.43422 49L46.5703 49Z"
                />
              </div>
            </div>
          </FloatingFocusManager>
        </>
      )}
    </StyledWish>
  );
};

export default Wish;
